import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Alert,
  Chip,
  Divider,
  CircularProgress
} from '@mui/material';
import { NativeBiometric, BiometryType } from '@capgo/capacitor-native-biometric';
import { Capacitor } from '@capacitor/core';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import SecurityIcon from '@mui/icons-material/Security';
import FaceIcon from '@mui/icons-material/Face';

function BiometricDebug() {
  const [debugInfo, setDebugInfo] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [testResults, setTestResults] = useState([]);

  const addTestResult = (test, result, error = null) => {
    const timestamp = new Date().toLocaleTimeString();
    setTestResults(prev => [{
      timestamp,
      test,
      result,
      error,
      success: !error
    }, ...prev.slice(0, 9)]); // 保留最近10個結果
  };

  const runPlatformCheck = () => {
    const info = {
      platform: Capacitor.getPlatform(),
      isNative: Capacitor.isNativePlatform(),
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString()
    };
    setDebugInfo(info);
    addTestResult('平台檢查', info);
  };

  const runAvailabilityCheck = async () => {
    setIsLoading(true);
    try {
      const result = await NativeBiometric.isAvailable();
      addTestResult('可用性檢查', result);
    } catch (error) {
      addTestResult('可用性檢查', null, error.message || error);
    } finally {
      setIsLoading(false);
    }
  };

  const runCredentialsCheck = async () => {
    setIsLoading(true);
    try {
      const result = await NativeBiometric.getCredentials({
        server: 'supercloud.com'
      });
      addTestResult('憑證檢查', result);
    } catch (error) {
      addTestResult('憑證檢查', null, error.message || error);
    } finally {
      setIsLoading(false);
    }
  };

  const runVerificationTest = async () => {
    setIsLoading(true);
    try {
      const result = await NativeBiometric.verifyIdentity({
        reason: '測試生物識別驗證功能',
        title: '生物識別測試',
        subtitle: 'SuperCloud Debug',
        description: '這是一個測試驗證'
      });
      addTestResult('驗證測試', result);
    } catch (error) {
      addTestResult('驗證測試', null, error.message || error);
    } finally {
      setIsLoading(false);
    }
  };

  const runSetCredentialsTest = async () => {
    setIsLoading(true);
    try {
      const result = await NativeBiometric.setCredentials({
        username: 'test_user',
        password: 'test_pass',
        server: 'supercloud.com'
      });
      addTestResult('設置憑證測試', result);
    } catch (error) {
      addTestResult('設置憑證測試', null, error.message || error);
    } finally {
      setIsLoading(false);
    }
  };

  const clearResults = () => {
    setTestResults([]);
    setDebugInfo({});
  };

  useEffect(() => {
    runPlatformCheck();
  }, []);

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        生物識別調試工具
      </Typography>
      
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            平台資訊
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Chip 
              label={`平台: ${debugInfo.platform || '未知'}`} 
              color={debugInfo.isNative ? 'success' : 'warning'}
            />
            <Chip 
              label={debugInfo.isNative ? '原生平台' : '網頁平台'} 
              color={debugInfo.isNative ? 'success' : 'warning'}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            User Agent: {debugInfo.userAgent}
          </Typography>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            測試功能
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Button 
              variant="outlined" 
              onClick={runPlatformCheck}
              disabled={isLoading}
            >
              平台檢查
            </Button>
            <Button 
              variant="outlined" 
              onClick={runAvailabilityCheck}
              disabled={isLoading}
            >
              可用性檢查
            </Button>
            <Button 
              variant="outlined" 
              onClick={runCredentialsCheck}
              disabled={isLoading}
            >
              憑證檢查
            </Button>
            <Button 
              variant="outlined" 
              onClick={runVerificationTest}
              disabled={isLoading}
            >
              驗證測試
            </Button>
            <Button 
              variant="outlined" 
              onClick={runSetCredentialsTest}
              disabled={isLoading}
            >
              設置憑證
            </Button>
            <Button 
              variant="outlined" 
              color="secondary"
              onClick={clearResults}
              disabled={isLoading}
            >
              清除結果
            </Button>
          </Box>
          {isLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">執行中...</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            測試結果 ({testResults.length})
          </Typography>
          {testResults.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              尚無測試結果
            </Typography>
          ) : (
            testResults.map((result, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Chip 
                    size="small"
                    label={result.test} 
                    color={result.success ? 'success' : 'error'}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {result.timestamp}
                  </Typography>
                </Box>
                {result.error ? (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    錯誤: {result.error}
                  </Alert>
                ) : (
                  <Alert severity="success" sx={{ mb: 1 }}>
                    成功: {JSON.stringify(result.result, null, 2)}
                  </Alert>
                )}
                {index < testResults.length - 1 && <Divider />}
              </Box>
            ))
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default BiometricDebug;